import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, TextField, Button, Stack, IconButton, InputAdornment } from '@mui/material';
import { Search, CalendarMonth, Clear as ClearIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';

const JobsFilter = ({ onApply }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => ({
    StartDate: searchParams.get('StartDate') || '',
    EndDate: searchParams.get('EndDate') || '',
    MixSearch: searchParams.get('MixSearch') || '',
  }), [searchParams]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (!next.get('SortBy')) { next.set('SortBy', 'id'); changed = true; }
    if (!next.get('SortOrder')) { next.set('SortOrder', 'desc'); changed = true; }
    if (!next.get('Status')) { next.set('Status', 'Preparing Vehicles'); changed = true; }
    if (changed) setSearchParams(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local input states; only applied when pressing Apply
  const [searchText, setSearchText] = useState(values.MixSearch);
  const [startDateLocal, setStartDateLocal] = useState(values.StartDate);
  const [endDateLocal, setEndDateLocal] = useState(values.EndDate);

  // keep inputs in sync when URL changes from elsewhere (cards, presets, reset)
  useEffect(() => {
    setSearchText(values.MixSearch);
  }, [values.MixSearch]);
  useEffect(() => {
    setStartDateLocal(values.StartDate);
    setEndDateLocal(values.EndDate);
  }, [values.StartDate, values.EndDate]);

  // Date validation helpers
  const startDate = startDateLocal;
  const endDate = endDateLocal;
  const endBeforeStart = !!(startDate && endDate && endDate < startDate);

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        const next = new URLSearchParams(searchParams);
        if (searchText) next.set('MixSearch', searchText); else next.delete('MixSearch');
        if (startDate) next.set('StartDate', startDate); else next.delete('StartDate');
        if (endDate) next.set('EndDate', endDate); else next.delete('EndDate');
        next.set('PageNumber', '0');
        setSearchParams(next);
        if (onApply) onApply();
      }}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="flex-end">
        {/* Search */}
        <Grid item xs={12} md={4}>
          <TextField
            size="small"
            fullWidth
            label="Search"
            placeholder="Booking / Client / Title / Desc"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchText('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>

        {/* Date range */}
        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <TextField
              type="date"
              size="small"
              fullWidth
              label="Start"
              InputLabelProps={{ shrink: true }}
              value={startDateLocal}
              onChange={(e) => {
                const newStart = e.target.value;
                if (!endDate) {
                  const plus7 = newStart ? new Date(new Date(newStart).getTime() + 7 * 24 * 60 * 60 * 1000) : null;
                  const plus7Str = plus7 ? plus7.toISOString().slice(0, 10) : '';
                  setStartDateLocal(newStart);
                  setEndDateLocal(plus7Str);
                } else if (endDate < newStart) {
                  setStartDateLocal(newStart);
                  setEndDateLocal(newStart);
                } else {
                  setStartDateLocal(newStart);
                }
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonth fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              type="date"
              size="small"
              fullWidth
              label="End"
              InputLabelProps={{ shrink: true }}
              value={endDateLocal}
              onChange={(e) => {
                const newEnd = e.target.value;
                if (startDate && newEnd && newEnd < startDate) {
                  // clamp to Start if user picks an earlier date
                  setEndDateLocal(startDate);
                } else {
                  setEndDateLocal(newEnd);
                }
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              inputProps={{ min: startDate || undefined }}
              error={endBeforeStart}
              helperText={endBeforeStart ? 'End must be on or after Start' : undefined}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonth fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Grid>

        {/* Actions */}
        <Grid item xs={12} md={3}>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ width: '100%', flexWrap: 'wrap' }}>
            <Button variant="contained" size="small" type="submit" disabled={endBeforeStart}>Apply</Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                ['StartDate', 'EndDate', 'MixSearch'].forEach((k) => next.delete(k));
                next.set('Status', 'Preparing Vehicles');
                next.set('PageNumber', '0');
                setStartDateLocal('');
                setEndDateLocal('');
                setSearchText('');
                setSearchParams(next);
                if (onApply) onApply();
              }}
            >
              Reset
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default JobsFilter;
